import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	FacebookConnection,
	SyncWarning
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { ConnectInstagramDialogComponent } from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/components/connect-channel/connect-instagram-dialog.component';
import { LoButton, LoDialogService, LoIcon } from 'lo-design-system';
import {
	MetaFbConnectionModalComponent,
	MetaFbConnectionModalData,
	MetaFbConnectionModalResult
} from './meta-fb-connection-modal.component';
import { MetaFbConnectionRowComponent } from './meta-fb-connection-row.component';
import { MetaSectionComponent } from './meta-section.component';

@Component({
	standalone: true,
	selector: 'meta-fb-connections-section',
	imports: [
		LoButton,
		LoIcon,
		MetaSectionComponent,
		MetaFbConnectionRowComponent
	],
	template: `
		<meta-section
			heading="Facebook connections"
			icon="facebook_logo"
			[count]="countLabel"
			[loading]="loading"
			[skeletonRows]="1"
		>
			<button
				slot="header-action"
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				(click)="onAdd()"
			>
				<lo-icon name="lo_plus" />
				Add Facebook
			</button>
			@for (conn of connections; track conn.id) {
				<meta-fb-connection-row
					[connection]="conn"
					[igCount]="igCountFor(conn.id)"
					[syncWarning]="syncWarningFor(conn.id)"
					(manage)="onManage(conn)"
				></meta-fb-connection-row>
			}
			@if (!connections.length) {
				<p class="empty lo-type-text-md">No Facebook connections yet.</p>
			}
		</meta-section>
	`,
	styles: [
		`
			:host {
				display: block;

				.empty {
					margin: 0;
					color: var(--lo-color-text-secondary);
					text-align: center;
				}
			}
		`
	]
})
export class MetaFbConnectionsSectionComponent {
	@Input() connections: FacebookConnection[] = [];
	@Input() igCountByConnection: Record<number, number> = {};
	@Input() syncWarnings: SyncWarning[] = [];
	@Input() loading = false;

	@Output() refresh = new EventEmitter<void>();

	private readonly dialogs = inject(LoDialogService);

	constructor(private dialog: MatDialog) {}

	get countLabel(): string {
		const active = (this.connections ?? []).filter(
			c => c.status === 'active'
		).length;
		return `${active} active`;
	}

	igCountFor(connectionId: number): number {
		return this.igCountByConnection[connectionId] ?? 0;
	}

	syncWarningFor(connectionId: number): SyncWarning | null {
		return (
			this.syncWarnings.find(w => w.connection_id === connectionId) ??
			null
		);
	}

	onAdd(): void {
		this.dialog
			.open(ConnectInstagramDialogComponent, {
				data: {
					allowedProviders: ['facebook_oauth'],
					redirectPath:
						'user-management/integrations/integration/instagram'
				}
			})
			.afterClosed()
			.subscribe(res => {
				if (res?.refreshed) this.refresh.emit();
			});
	}

	onManage(conn: FacebookConnection): void {
		const igCount = this.igCountByConnection[conn.id] ?? 0;
		const ref = this.dialogs.open<
			MetaFbConnectionModalComponent,
			MetaFbConnectionModalData,
			MetaFbConnectionModalResult
		>(MetaFbConnectionModalComponent, {
			data: { connection: conn, igCount }
		});
		// CONTRACT: Escape and a backdrop click close the modal without a
		// result, so the discovery walk it may have run is read off the instance.
		const modal = ref.componentInstance;
		ref.closed.subscribe(res => {
			if (res?.refreshed || res?.removed || modal?.discoveryRan)
				this.refresh.emit();
		});
	}
}
